import React, { useRef, useState } from "react";
import {
  Button,
  CircularProgress,
  MenuItem,
  Select,
  styled,
} from "@mui/material";
import { ClinicFormInput } from "../elements/clinicFormInput";
import { ClinicActionButton } from "fe-web-components/clinic/clinicActionButton";
import { createClinicPatientCase } from "./createClinicPatientCase";
import { InviteData } from "../util/sendInvitaion";
import {
  ATHENA_GENDER_FEMALE,
  ATHENA_GENDER_MALE,
} from "../special/importMaccabiPatients/patientVisitData";
import { useClinicContext } from "fe-web-components/clinic/ClinicContext";
import { Constraints } from "shared";
import { useDaoContext } from "fe-web-components/diseaseview/DaoContext";
import {
  SavedCaseStatus,
  SummarySectionType,
  UpdateSummaryErrors,
} from "fe-common/graphql";
import { ReactComponent as DiagnoseIcon } from "../../../assets/clinic/diagnoseIcon.svg";
import { checkSummaryForPII } from "fe-web-components/patientCard/patientSummary";
import { TextHighlight } from "fe-common/commonTypes/errors";
import { FormInput } from "fe-web-components/layout/formInput";
import { PatientSummaryError } from "fe-web-components/patientCard/patientSummaryError";
import { useClinicState } from "../../../clinic/clinicStateContext";

//region [[ Styles ]]
const FormLine = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: 16,
  "&.spaced": {
    justifyContent: "space-between",
  },
});

const FormFields = styled("div")({
  marginTop: 20,
  display: "flex",
  flexDirection: "column",
  alignItems: "start",
  gap: 12,
});

const Remark = styled("div")({
  fontSize: 13,
  marginTop: -6,
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface NewCaseFormProps {
  clinicId: string;
  onSave: (newCaseDigitCode: string) => void;
  onError: (message: string) => void;
  showExample?: boolean;
}

enum CaseSteps {
  CREATING = "Creating new case",
  UPDATING = "Applying case description",
  REDIRECTING = "Opening patient card",
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const ManualCaseForm = ({
  clinicId,
  onSave,
  onError,
  showExample,
}: NewCaseFormProps) => {
  const daoClinic = useClinicContext();
  const dao = useDaoContext();
  const [{ clinicConfig }] = useClinicState();
  const [chiefComplaint, setChiefComplaint] = useState<string>("");
  const [fullName, setFullName] = useState<string>("");
  const [age, setAge] = useState<string>(null);
  const [gender, setGender] = useState<
    typeof ATHENA_GENDER_MALE | typeof ATHENA_GENDER_FEMALE
  >(null);
  const [description, setDescription] = useState<string>();
  const [caseStep, setCaseStep] = useState<CaseSteps>(null);
  const [errorMsgs, setErrorMsgs] = useState<React.ReactNode[]>(null);
  const [complaintHighlights, setComplaintHighlights] = useState<
    TextHighlight[]
  >(null);
  const [descriptionHighlights, setDescriptionHighlights] = useState<
    TextHighlight[]
  >(null);

  const MIN_AGE = Constraints.MinAge;
  const MAX_AGE = Constraints.MaxAge;
  const complaintOffset = useRef(0);
  const descriptionOffset = useRef(0);
  const PII_CHECK_ACTIVE = clinicConfig.standalone.checkPIIWarning;

  const validInput = (): boolean => {
    let message = "";
    if (age) {
      if (isNaN(parseFloat(age))) message = "Age must be numeric";
      else if (parseFloat(age) < MIN_AGE || parseFloat(age) > MAX_AGE)
        message = `Age must be between ${MIN_AGE} and ${MAX_AGE}`;
    }
    if (message) setErrorMsgs([message]);
    return !message;
  };

  const fillExample = () => {
    setDescriptionHighlights(null);
    setErrorMsgs(null);
    setFullName("Example Patient");
    setChiefComplaint("Shortness of breath");
    setAge("40");
    setGender(ATHENA_GENDER_MALE);
    setDescription(
      "Gender: Male\n\n" +
        "Medical History: Hyperlipidemia\n\n" +
        "History of Present Illness (HPI):\n" +
        "The patient, a 40-year-old male with a history of hyperlipidemia, presents with complaints of shortness of breath and chest discomfort escalating over the past day. He notes experiencing exacerbation of symptoms while supine, with relief upon assuming a seated position and consuming coffee. The onset coincides with a recent viral respiratory infection endured last week, characterized by fever, nasal congestion, and fatigue, which resolved two days ago. Notably, the patient denies cough or persistent shortness of breath during the preceding illness. Presently, he denies any chronic medications other than those prescribed for hyperlipidemia.\n\n" +
        "Vital Signs:\n" +
        "Body Temperature: 37.4°C\n" +
        "Blood Pressure: 120/80 mmHg\n" +
        "Heart Rate: Within normal limits",
    );
  };

  const handleClear = () => {
    setErrorMsgs(null);
    setFullName("");
    setChiefComplaint("");
    setAge(null);
    setGender(null);
    setDescription("");
  };

  const buildPrompt = (): string => {
    let mainInfo = `${age ? age + " year-old" : ""}${
      gender ? (gender === ATHENA_GENDER_MALE ? " male" : " female") : ""
    }${chiefComplaint ? " presenting with " : ""}`;
    complaintOffset.current = mainInfo.length; //used for highlighting chiefComplaint input
    mainInfo = mainInfo + chiefComplaint + (mainInfo ? ".\n" : "");
    descriptionOffset.current = mainInfo.length; // use for placing highlight in description
    return mainInfo + description;
  };

  const handleSave = async () => {
    setErrorMsgs(null);
    if (!validInput()) return null;
    let caseId = null;
    setCaseStep(CaseSteps.CREATING);
    const patientName = fullName ? fullName.split(" ").filter(Boolean) : [""];
    const inviteData: InviteData = {
      patient: {
        firstName: patientName[0],
        lastName: patientName.length > 1 ? patientName.slice(1).join(" ") : "",
        email: null,
        phoneNumber: null,
        gender,
        age: parseInt(age),
      },
      visitDateTime: null,
      isNew: true,
    };

    try {
      if (PII_CHECK_ACTIVE) {
        //Check for PII in summary
        const PIIValidation = await checkSummaryForPII(buildPrompt(), dao);
        if (!PIIValidation.valid) {
          setErrorMsgs(PIIValidation.messages);
          const compHighlights = PIIValidation.highlights.filter(
            (hl) =>
              hl.EndOffset - complaintOffset.current <=
              descriptionOffset.current,
          );
          setComplaintHighlights(
            compHighlights.map((hl) => ({
              ...hl,
              BeginOffset: hl.BeginOffset - complaintOffset.current,
              EndOffset: hl.EndOffset - complaintOffset.current,
            })),
          );
          const descHighlights = PIIValidation.highlights.filter(
            (hl) => hl.BeginOffset >= descriptionOffset.current,
          );
          setDescriptionHighlights(
            descHighlights.map((hl) => ({
              ...hl,
              BeginOffset: hl.BeginOffset - descriptionOffset.current,
              EndOffset: hl.EndOffset - descriptionOffset.current,
            })),
          );
          setCaseStep(null);
          return;
        }
      }

      //create new case
      const newCase = await createClinicPatientCase(
        clinicId,
        inviteData,
        chiefComplaint,
      );
      caseId = newCase.caseId;

      //update case status and chief complaint
      setCaseStep(CaseSteps.UPDATING);
      //set summary
      const summaryUpdate = await dao.updatePatientSummary(caseId, clinicId, {
        sections: [
          {
            title: "",
            content: buildPrompt(),
            type: SummarySectionType.FreeForm,
          },
        ],
      });

      if (summaryUpdate?.status?.toUpperCase() !== SavedCaseStatus.Success) {
        caseId = null;
        setErrorMsgs(
          summaryUpdate.errors?.map((e: UpdateSummaryErrors) => e.kahun_error),
        );
        setCaseStep(null);
      } else {
        //get case digit code
        setCaseStep(CaseSteps.REDIRECTING);
        await daoClinic
          .makeClinicShortCode(clinicId, caseId) //set case as isFinished and get digitCode
          .then((digitCode) => {
            onSave(digitCode);
          })
          .catch((e) => console.error(e));
      }
    } catch (e) {
      console.error(e);
      onError("Error " + caseStep);
      caseId = null;
      setCaseStep(null);
    }
  };

  return (
    <>
      <FormLine className="spaced">
        <strong>Manual Case</strong>
        {showExample && (
          <Button
            title={"example"}
            endIcon={<DiagnoseIcon style={{ height: 20, width: 14 }} />}
            onClick={() => fillExample()}
            variant={"text"}
            size={"small"}
            color={"primary"}
            style={{ textTransform: "none" }}
          >
            Example
          </Button>
        )}
      </FormLine>
      <FormFields>
        <FormInput
          id="chiefComplaint"
          label="Chief complaint"
          value={chiefComplaint}
          onChange={(e) => setChiefComplaint(e.target.value)}
          highlights={complaintHighlights}
          style={{ width: "100%" }}
        />
        <FormLine className="spaced">
          {clinicConfig.standalone.showPatientName && (
            <ClinicFormInput
              style={{ width: "100%" }}
              id="patientFullName"
              label="Patient Full Name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          )}
          <ClinicFormInput
            id="patientAge"
            label="Patient age"
            style={{ minWidth: 90 }}
            value={age || ""}
            type={"number"}
            onChange={(e) => setAge(e.target.value)}
          />
          <ClinicFormInput
            id="gender"
            label="Patient Gender"
            style={{ minWidth: 190 }}
            customInput={
              <Select
                id="patientGender"
                value={gender || ""}
                variant={"outlined"}
                onChange={(e) =>
                  setGender(
                    e.target.value as
                      | typeof ATHENA_GENDER_FEMALE
                      | typeof ATHENA_GENDER_MALE,
                  )
                }
                fullWidth
                placeholder={"Select"}
              >
                <MenuItem value={ATHENA_GENDER_MALE} key={"genderMale"}>
                  Male
                </MenuItem>
                <MenuItem value={ATHENA_GENDER_FEMALE} key={"genderFemale"}>
                  Female
                </MenuItem>
              </Select>
            }
          />
        </FormLine>
        <FormInput
          id="caseDescription"
          label="Case Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          minRows={6}
          maxRows={6}
          highlights={descriptionHighlights}
          style={{ width: "100%" }}
        />
        <Remark>*Do not include personal identifiable information</Remark>
        {errorMsgs && <PatientSummaryError errorMessages={errorMsgs} />}
      </FormFields>
      <FormLine className="spaced" style={{ marginTop: 14 }}>
        <ClinicActionButton
          onClick={handleClear}
          variant={"outlined"}
          label={"Clear"}
          lightMode
          disabled={
            !!caseStep ||
            (!age && !chiefComplaint && !description && !gender && !fullName)
          }
        />
        <ClinicActionButton
          onClick={handleSave}
          variant="contained"
          label={!caseStep ? "Generate Insights" : caseStep}
          icon={!!caseStep && <CircularProgress size={16} />}
          color={"primary"}
          disabled={!description || !!caseStep}
        />
      </FormLine>
    </>
  );
};
